import React, { useState, useEffect, useCallback } from 'react';
import { API_V1, authFetch } from '../constants';
import { useToast } from '../contexts/ToastContext';
import KpiCard from '../components/ui/KpiCard';
import PageLayout from '../components/ui/PageLayout';
import FilterBar from '../components/ui/FilterBar';
import DataTable, { Column } from '../components/ui/DataTable';
import ActionButton from '../components/ui/ActionButton';

interface Account {
  id: number;
  account_code: string;
  account_name: string;
  standard_type: string;
  standard_subtype: string | null;
  statement: string;
  normal_balance: string;
  is_intercompany: boolean;
  is_control_account: boolean;
  control_subledger: string | null;
  mapping_status: string;
  mapping_confidence: number | null;
  mapping_source: string | null;
  entity_code: string | null;
  is_suspense: boolean;
  is_clearing: boolean;
}

interface MappingStats {
  total_accounts: number;
  mapped: number;
  auto_mapped: number;
  unmapped: number;
  mapping_rate_pct: number;
  by_type: Record<string, number>;
  control_accounts: Record<string, number>;
  intercompany_accounts: number;
}

const statusColors: Record<string, string> = {
  UNMAPPED: 'var(--negative)',
  AUTO_MAPPED: 'var(--warning)',
  MANUAL: 'var(--navy-800)',
  VERIFIED: 'var(--positive)',
};

const typeColors: Record<string, string> = {
  ASSET: '#0dcaf0',
  LIABILITY: '#ffc107',
  EQUITY: '#198754',
  REVENUE: '#0d6efd',
  EXPENSE: '#dc3545',
  UNKNOWN: '#6c757d',
};

export default function CoAMapping() {
  const { toast } = useToast();
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [stats, setStats] = useState<MappingStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ type: '', status: '', entity: '' });
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [editing, setEditing] = useState<Account | null>(null);
  const [editForm, setEditForm] = useState({
    standard_type: '', standard_subtype: '', is_intercompany: false,
    is_control_account: false, control_subledger: '', normal_balance: '',
  });
  const [uploading, setUploading] = useState(false);
  const [autoMapping, setAutoMapping] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const fetchAccounts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), per_page: '50' });
      if (filter.type) params.set('standard_type', filter.type);
      if (filter.status) params.set('mapping_status', filter.status);
      if (filter.entity) params.set('entity_code', filter.entity);
      const resp = await authFetch(`${API_V1}/coa/accounts?${params}`);
      if (!resp.ok) { setAccounts([]); setTotal(0); setLoading(false); return; }
      const data = await resp.json();
      setAccounts(data.accounts || []);
      setTotal(data.total || 0);
      if (data.mapping_stats) setStats(data.mapping_stats);
    } catch (e) { console.error(e); toast({ message: 'Failed to load accounts.', type: 'error' }); }
    setLoading(false);
  }, [page, filter]);

  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);

  const handleUpload = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    setUploading(true);
    try {
      const resp = await authFetch(`${API_V1}/coa/upload`, { method: 'POST', body: formData });
      const data = await resp.json();
      toast({ message: `Imported: ${data.imported}, Auto-mapped: ${data.auto_mapped}, Needs review: ${data.needs_review}`, type: 'success' });
      fetchAccounts();
    } catch (err) { toast({ message: 'Upload failed. Please check the file and try again.', type: 'error' }); }
    setUploading(false);
  };

  const handleAutoMap = async () => {
    setAutoMapping(true);
    try {
      const resp = await authFetch(`${API_V1}/coa/auto-map`, {
        method: 'POST', headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({ entity_code: filter.entity || null }),
      });
      const data = await resp.json();
      toast({ message: `Auto-mapped: ${data.auto_mapped}, Needs review: ${data.needs_review}`, type: 'success' });
      fetchAccounts();
    } catch (err) { toast({ message: 'Auto-map failed. Please try again.', type: 'error' }); }
    setAutoMapping(false);
  };

  const handleSaveMapping = async () => {
    if (!editing) return;
    try {
      await authFetch(`${API_V1}/coa/accounts/${editing.id}`, {
        method: 'PUT', headers: {'Content-Type': 'application/json'},
        body: JSON.stringify(editForm),
      });
      setEditing(null);
      fetchAccounts();
    } catch (err) { toast({ message: 'Save failed. Please try again.', type: 'error' }); }
  };

  const handleVerify = async (id: number) => {
    await authFetch(`${API_V1}/coa/bulk-verify`, {
      method: 'POST', headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({ account_ids: [id] }),
    });
    fetchAccounts();
  };

  const handleBulkVerify = async () => {
    if (selected.size === 0) return;
    await authFetch(`${API_V1}/coa/bulk-verify`, {
      method: 'POST', headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({ account_ids: Array.from(selected) }),
    });
    setSelected(new Set());
    fetchAccounts();
  };

  const openEdit = (acct: Account) => {
    setEditing(acct);
    setEditForm({
      standard_type: acct.standard_type || '',
      standard_subtype: acct.standard_subtype || '',
      is_intercompany: acct.is_intercompany,
      is_control_account: acct.is_control_account,
      control_subledger: acct.control_subledger || '',
      normal_balance: acct.normal_balance || '',
    });
  };

  const toggleSelect = (id: number) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  const subtypes: Record<string, string[]> = {
    ASSET: ['CASH', 'RECEIVABLES', 'INVENTORY', 'PREPAID', 'PP_AND_E', 'INTANGIBLE', 'INVESTMENT', 'OTHER_ASSET'],
    LIABILITY: ['PAYABLES', 'ACCRUED', 'DEBT', 'DEFERRED_REVENUE', 'TAX_PAYABLE', 'OTHER_LIABILITY'],
    EQUITY: ['SHARE_CAPITAL', 'RETAINED_EARNINGS', 'OTHER_EQUITY', 'CTA'],
    REVENUE: ['OPERATING_REVENUE', 'OTHER_INCOME', 'INTEREST_INCOME'],
    EXPENSE: ['COGS', 'OPERATING_EXPENSE', 'DEPRECIATION', 'INTEREST_EXPENSE', 'TAX_EXPENSE', 'OTHER_EXPENSE'],
  };

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '7px 12px', fontSize: 13,
    border: '1px solid var(--border-subtle)', borderRadius: 6,
    background: 'var(--bg-card)', color: 'var(--text-primary)', outline: 'none',
  };

  const tableColumns: Column[] = [
    { key: 'select', label: '', width: 40, render: (_: any, row: Account) => (
      <input type="checkbox" checked={selected.has(row.id)} onChange={() => toggleSelect(row.id)} />
    )},
    { key: 'account_code', label: 'Code', render: (v: string) => <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{v}</span> },
    { key: 'account_name', label: 'Name' },
    { key: 'standard_type', label: 'Type', render: (v: string) => (
      <span style={{ background: `${typeColors[v] || '#6c757d'}20`, color: typeColors[v] || '#6c757d', padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600 }}>{v}</span>
    )},
    { key: 'standard_subtype', label: 'Subtype', render: (v: string | null) => <span style={{ fontSize: 12, color: 'var(--text-hint)' }}>{v || '-'}</span> },
    { key: 'mapping_status', label: 'Status', render: (v: string) => (
      <span style={{ background: `${statusColors[v] || '#6c757d'}15`, color: statusColors[v] || '#6c757d', padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600 }}>{v}</span>
    )},
    { key: 'mapping_confidence', label: 'Confidence', render: (v: number | null) => v != null ? `${v}%` : '-' },
    { key: 'flags', label: 'Flags', render: (_: any, row: Account) => (
      <span style={{ fontSize: 11 }}>
        {row.is_intercompany && <span style={{ background: '#6610f220', color: '#6610f2', padding: '1px 6px', borderRadius: 8, marginRight: 4, fontWeight: 600 }}>IC</span>}
        {row.is_control_account && <span style={{ background: '#fd7e1420', color: '#fd7e14', padding: '1px 6px', borderRadius: 8, marginRight: 4, fontWeight: 600 }}>{row.control_subledger || 'CTRL'}</span>}
        {row.is_suspense && <span style={{ background: '#6c757d20', color: '#6c757d', padding: '1px 6px', borderRadius: 8, fontWeight: 600 }}>SUSP</span>}
      </span>
    )},
    { key: 'actions', label: 'Actions', render: (_: any, row: Account) => (
      <div style={{ display: 'flex', gap: 4 }}>
        <ActionButton label="Map" onClick={() => openEdit(row)} variant="secondary" size="sm" />
        {row.mapping_status === 'AUTO_MAPPED' && (
          <ActionButton label="Verify" onClick={() => handleVerify(row.id)} variant="success" size="sm" />
        )}
      </div>
    )},
  ];

  return (
    <PageLayout title="Chart of Accounts Mapping">
      {/* Stats Cards */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 14, marginBottom: 24 }}>
          <KpiCard label="Total Accounts" value={stats.total_accounts} valueColor="var(--text-primary)" />
          <KpiCard label="Mapped" value={`${stats.mapping_rate_pct}%`} valueColor="var(--positive)" />
          <KpiCard label="Unmapped" value={stats.unmapped} valueColor="var(--negative)" />
          <KpiCard label="Auto-Mapped" value={stats.auto_mapped} valueColor="var(--warning)" />
          <KpiCard label="Intercompany" value={stats.intercompany_accounts} valueColor="var(--text-primary)" />
        </div>
      )}

      {/* Upload Section */}
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: 8, padding: 20, marginBottom: 24 }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy-900)', marginTop: 0 }}>Upload Chart of Accounts</h3>
        <form onSubmit={handleUpload} style={{ display: 'flex', gap: 12, alignItems: 'end', flexWrap: 'wrap' }}>
          <div>
            <label style={{ display: 'block', fontSize: 13, marginBottom: 4, color: 'var(--text-secondary)' }}>CoA File (CSV/XLSX)</label>
            <input type="file" name="file" accept=".csv,.xlsx" required />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 13, marginBottom: 4, color: 'var(--text-secondary)' }}>Entity Code (optional)</label>
            <input type="text" name="entity_code" placeholder="e.g. 1000" style={inputStyle} />
          </div>
          <ActionButton label={uploading ? 'Uploading...' : 'Upload & Auto-Map'} onClick={() => {}} variant="primary" disabled={uploading} />
        </form>
      </div>

      {/* Filters + Actions */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <FilterBar filters={[
          {
            key: 'type', label: 'Type', type: 'select', value: filter.type,
            onChange: v => { setFilter({...filter, type: v}); setPage(1); },
            options: ['ASSET','LIABILITY','EQUITY','REVENUE','EXPENSE','UNKNOWN'].map(t => ({ value: t, label: t })),
          },
          {
            key: 'status', label: 'Status', type: 'select', value: filter.status,
            onChange: v => { setFilter({...filter, status: v}); setPage(1); },
            options: ['UNMAPPED','AUTO_MAPPED','MANUAL','VERIFIED'].map(s => ({ value: s, label: s })),
          },
        ]} />
        <ActionButton label={autoMapping ? 'Mapping...' : 'Auto-Map Remaining'} onClick={handleAutoMap} disabled={autoMapping} variant="secondary" />
        {selected.size > 0 && (
          <ActionButton label={`Verify Selected (${selected.size})`} onClick={handleBulkVerify} variant="success" />
        )}
      </div>

      {/* Account Table */}
      <DataTable columns={tableColumns} rows={accounts} rowKey="id" loading={loading} />

      {/* Pagination */}
      <div style={{ padding: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 13, color: 'var(--text-hint)' }}>Showing {accounts.length} of {total}</span>
        <div style={{ display: 'flex', gap: 8 }}>
          <ActionButton label="Prev" onClick={() => setPage(p => p - 1)} disabled={page <= 1} variant="secondary" size="sm" />
          <span style={{ padding: '4px 8px', fontSize: 13, color: 'var(--text-secondary)' }}>Page {page}</span>
          <ActionButton label="Next" onClick={() => setPage(p => p + 1)} disabled={accounts.length < 50} variant="secondary" size="sm" />
        </div>
      </div>

      {/* Edit Side Panel */}
      {editing && (
        <div style={{ position: 'fixed', top: 0, right: 0, width: 380, height: '100vh',
          background: 'var(--bg-card)', boxShadow: '-2px 0 8px rgba(0,0,0,0.1)', padding: 24, overflowY: 'auto', zIndex: 1000,
          border: '1px solid var(--border-subtle)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <h3 style={{ margin: 0, color: 'var(--navy-900)', fontSize: 16, fontWeight: 700 }}>Map Account</h3>
            <button onClick={() => setEditing(null)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--text-hint)' }}>X</button>
          </div>
          <p style={{ fontFamily: 'monospace', fontWeight: 'bold', color: 'var(--navy-900)' }}>{editing.account_code}</p>
          <p style={{ color: 'var(--text-secondary)' }}>{editing.account_name}</p>

          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 13, marginBottom: 4, color: 'var(--text-secondary)' }}>Standard Type</label>
            <select value={editForm.standard_type} onChange={e => setEditForm({...editForm, standard_type: e.target.value, standard_subtype: ''})} style={inputStyle}>
              <option value="">Select...</option>
              {['ASSET','LIABILITY','EQUITY','REVENUE','EXPENSE'].map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 13, marginBottom: 4, color: 'var(--text-secondary)' }}>Subtype</label>
            <select value={editForm.standard_subtype} onChange={e => setEditForm({...editForm, standard_subtype: e.target.value})} style={inputStyle}>
              <option value="">Select...</option>
              {(subtypes[editForm.standard_type] || []).map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 13, marginBottom: 4, color: 'var(--text-secondary)' }}>Normal Balance</label>
            <select value={editForm.normal_balance} onChange={e => setEditForm({...editForm, normal_balance: e.target.value})} style={inputStyle}>
              <option value="">Select...</option>
              <option value="DEBIT">DEBIT</option>
              <option value="CREDIT">CREDIT</option>
            </select>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 13, color: 'var(--text-secondary)' }}><input type="checkbox" checked={editForm.is_intercompany}
              onChange={e => setEditForm({...editForm, is_intercompany: e.target.checked})} /> Intercompany</label>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 13, color: 'var(--text-secondary)' }}><input type="checkbox" checked={editForm.is_control_account}
              onChange={e => setEditForm({...editForm, is_control_account: e.target.checked})} /> Control Account</label>
          </div>
          {editForm.is_control_account && (
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: 13, marginBottom: 4, color: 'var(--text-secondary)' }}>Subledger</label>
              <select value={editForm.control_subledger} onChange={e => setEditForm({...editForm, control_subledger: e.target.value})} style={inputStyle}>
                <option value="">Select...</option>
                {['AP','AR','FA','INV'].map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          )}

          <ActionButton label="Save Mapping" onClick={handleSaveMapping} variant="primary" />
        </div>
      )}
    </PageLayout>
  );
}
